import { isAxiosError } from 'axios';

/**
 * Smart retry configuration for React Query
 *
 * Retry strategy:
 * - 5xx errors (server errors): Retry with exponential backoff
 * - Network errors: Retry with exponential backoff
 * - 4xx errors (client errors): Don't retry (except 408, 429)
 * - Auth errors (401, 403): Don't retry
 */
export const shouldRetryRequest = (failureCount: number, error: unknown): boolean => {
  // Max 3 retries
  if (failureCount >= 3) {
    return false;
  }

  // If it's an Axios error, check the status code
  if (isAxiosError(error)) {
    const status = error.response?.status;

    // No status means network error - retry
    if (!status) {
      return true;
    }

    // 408 Request Timeout - retry
    if (status === 408) {
      return true;
    }

    // 429 Too Many Requests - retry with backoff
    if (status === 429) {
      return true;
    }

    // 5xx Server Errors - retry
    if (status >= 500 && status < 600) {
      return true;
    }

    // 4xx Client Errors (except 408, 429) - don't retry
    if (status >= 400 && status < 500) {
      return false;
    }
  }

  // For other errors, retry
  return true;
};

/**
 * Exponential backoff delay calculation
 *
 * Attempt 1: 1 second
 * Attempt 2: 2 seconds
 * Attempt 3: 4 seconds
 * Max: 30 seconds
 */
export const getRetryDelay = (attemptIndex: number): number => {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
};

/**
 * Standard retry configuration for query hooks
 * Use this for GET requests that fetch data
 */
export const defaultQueryRetry = {
  retry: shouldRetryRequest,
  retryDelay: getRetryDelay,
};

/**
 * Aggressive retry configuration for critical queries
 * Use this for essential data like auth, chat credentials
 */
export const criticalQueryRetry = {
  retry: (failureCount: number, error: unknown) => {
    // Allow up to 5 retries for critical queries
    if (failureCount >= 5) {
      return false;
    }
    return shouldRetryRequest(failureCount, error);
  },
  retryDelay: getRetryDelay,
};

/**
 * No retry configuration
 * Use this for mutations or operations that shouldn't be retried
 */
export const noRetry = {
  retry: false,
};

/**
 * Mutation retry configuration for idempotent operations
 * Use sparingly - only for mutations that are safe to retry
 */
export const idempotentMutationRetry = {
  retry: (failureCount: number, error: unknown) => {
    // Only retry once for mutations
    if (failureCount >= 1) {
      return false;
    }
    return shouldRetryRequest(failureCount, error);
  },
  retryDelay: getRetryDelay,
};
